import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Radio } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import CodeEditor from '@/components/CodeEditor';
import { maasRequiredRules, maasRequiredSelectRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';

import { isFieldDisabled, isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { COMMAND_ARGS_NORMAL_FIELD_KEYS } from './fieldKeys';
import styles from './index.module.less';
import { resolveCommandArgsNormalPaths } from './resolvePaths';
import type { CommandArgsNormalBlockProps, CommandShellType } from './types';
import { applyShellPrefill, initCommandApiToFormFields, initCommandFormFieldsToApi, isShellPrefillText, scriptTextToArgs } from './utils';

const FormItem = Form.Item;

const SHELL_OPTIONS: { label: string; value: CommandShellType }[] = [
  { label: 'Bash', value: 'bash' },
  { label: 'Shell', value: 'sh' },
];

interface ScriptCodeEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

/** 脚本输入：复用 CodeEditor，整段内容写入 args[0] */
function ScriptCodeEditor({ value, onChange, disabled }: ScriptCodeEditorProps) {
  return (
    <div className={styles.scriptEditorWrap}>
      <CodeEditor
        value={value ?? ''}
        mode="sh"
        theme="textmate"
        readOnly={disabled}
        className={styles.scriptEditor}
        options={{ minLines: 6, maxLines: 20 }}
        onChange={disabled ? undefined : onChange}
      />
    </div>
  );
}

function useShellType(form: FormInstance, shellTypePath: string): CommandShellType {
  const shellType = Form.useWatch(shellTypePath, form) as CommandShellType | undefined;
  return shellType ?? 'bash';
}

/** 通用命令+脚本输入：Bash/Shell 类型选择 + 只读启动命令 + 脚本编辑器 */
export function CommandArgsNormalBlock(props: CommandArgsNormalBlockProps) {
  const { form, paths, options, ...control } = props;
  const { t } = useTranslation();
  const noStyle = options?.noStyle === true;
  const required = options?.required === true;
  const scriptHint = options?.scriptHint;
  const readonly = isFieldReadonly(COMMAND_ARGS_NORMAL_FIELD_KEYS.commands, control);
  const disabled = isFieldDisabled(COMMAND_ARGS_NORMAL_FIELD_KEYS.commands, control);
  const shellType = useShellType(form, paths.shellType);
  const commandLabel = options?.commandLabel ?? t('components:startup.command');
  const shellTypeRules = required ? maasRequiredSelectRules(t('data-center:maas.command.type')) : undefined;
  const commandsRules = required ? maasRequiredRules(commandLabel) : undefined;
  const scriptRules = required ? maasRequiredRules(t('data-center:maas.command.script')) : undefined;
  const commandsDisabled = true;
  /** 预览态不展示可交互提示，避免只读确认页噪音 */
  const showScriptHint = !readonly && !!scriptHint;
  const scriptLabel = showScriptHint ? (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {t('data-center:maas.command.script')}
      {scriptHint}
    </div>
  ) : (
    t('data-center:maas.command.script')
  );

  const handleShellTypeChange = useCallback(
    (type: CommandShellType) => {
      form.setFieldValue(paths.shellType, type);
      form.setFieldValue(paths.commands, applyShellPrefill(type));
    },
    [form, paths.commands, paths.shellType],
  );

  useEffect(() => {
    const currentShellType = (form.getFieldValue(paths.shellType) as CommandShellType | undefined) ?? 'bash';
    if (!form.getFieldValue(paths.shellType)) {
      form.setFieldValue(paths.shellType, currentShellType);
    }
    const currentCommands = String(form.getFieldValue(paths.commands) ?? '').trim();
    if (!currentCommands || isShellPrefillText(currentCommands)) {
      form.setFieldValue(paths.commands, applyShellPrefill(currentShellType));
    }
  }, [form, paths.commands, paths.shellType]);

  if (!isFieldVisible(COMMAND_ARGS_NORMAL_FIELD_KEYS.commands, control)) {
    return null;
  }

  return (
    <>
      <FormItem
        field={paths.shellType}
        label={t('data-center:maas.command.type')}
        noStyle={noStyle}
        triggerPropName="value"
        rules={shellTypeRules}
      >
        <Radio.Group
          type="button"
          size="small"
          disabled={readonly || disabled}
          options={SHELL_OPTIONS}
          value={shellType}
          onChange={handleShellTypeChange}
        />
      </FormItem>
      <FormItem field={paths.commands} noStyle={noStyle} rules={commandsRules}>
        <Input
          disabled={commandsDisabled || readonly || disabled}
          placeholder={t('data-center:access.config.startup.command.placeholder')}
        />
      </FormItem>
      <FormItem label={scriptLabel} field={paths.args} noStyle={noStyle} rules={scriptRules}>
        <ScriptCodeEditor disabled={readonly || disabled} />
      </FormItem>
    </>
  );
}

/** 初始化 shell 类型默认值（编辑回填或新建时使用） */
export function ensureCommandArgsNormalDefaults(
  form: CommandArgsNormalBlockProps['form'],
  paths: CommandArgsNormalBlockProps['paths'],
  initCommand?: { commands?: string[]; args?: string[] } | null,
) {
  const shellTypePath = paths.shellType;
  const commandsPath = paths.commands;
  const argsPath = paths.args;
  const hasShellType = form.getFieldValue(shellTypePath) !== undefined;
  const hasCommands = String(form.getFieldValue(commandsPath) ?? '').trim().length > 0;
  const hasScript = String(form.getFieldValue(argsPath) ?? '').trim().length > 0;

  if (initCommand?.commands?.length || initCommand?.args?.length) {
    const fields = initCommandApiToFormFields({
      commands: initCommand.commands ?? [],
      args: initCommand.args,
    });
    if (fields._shellType) form.setFieldValue(shellTypePath, fields._shellType);
    if (fields._commandsText) form.setFieldValue(commandsPath, fields._commandsText);
    if (fields._argsText !== undefined) form.setFieldValue(argsPath, fields._argsText);
    return;
  }

  const shellType = ((hasShellType ? form.getFieldValue(shellTypePath) : 'bash') ?? 'bash') as CommandShellType;
  if (!hasShellType) {
    form.setFieldValue(shellTypePath, shellType);
  }
  if (!hasCommands || isShellPrefillText(form.getFieldValue(commandsPath))) {
    form.setFieldValue(commandsPath, applyShellPrefill(shellType));
  }
  if (!hasScript && !hasCommands && !hasShellType) {
    form.setFieldValue(commandsPath, applyShellPrefill(shellType));
  }
}

/** 从表单 paths 序列化为 API initCommand */
export function readInitCommandFromFormPaths(form: CommandArgsNormalBlockProps['form'], paths: CommandArgsNormalBlockProps['paths']) {
  return initCommandFormFieldsToApi({
    _shellType: form.getFieldValue(paths.shellType),
    _commandsText: form.getFieldValue(paths.commands),
    _argsText: form.getFieldValue(paths.args),
  });
}

export { readInitCommandFieldsFromForm } from './formHelpers';

/** 清空 initCommand 相关表单字段 */
export function resetInitCommandFormFields(form: CommandArgsNormalBlockProps['form'], prefix = 'initCommand'): void {
  const paths = resolveCommandArgsNormalPaths(prefix);
  form.setFieldValue(paths.shellType, undefined);
  form.setFieldValue(paths.commands, undefined);
  form.setFieldValue(paths.args, undefined);
  form.setFieldValue(prefix, undefined);
}

/** 判断表单 paths 是否有 initCommand 内容 */
export function hasInitCommandAtFormPaths(form: CommandArgsNormalBlockProps['form'], paths: CommandArgsNormalBlockProps['paths']) {
  return scriptTextToArgs(form.getFieldValue(paths.args)).length > 0;
}
